import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Key, Shield, UserCircle, Plus, Trash2, Edit2, AlertTriangle, Settings2, Headphones, Mail, Send, Copy } from 'lucide-react';
import { toast } from 'sonner';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('all');
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRolesDialogOpen, setIsRolesDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Custom role form
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', permissions: [] });

  // Invite form
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'reviewer', custom_role_id: '', team_id: '' });
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'reviewer',
    team_id: '',
    custom_role_id: ''
  });
  
  // Edit user form
  const [editUser, setEditUser] = useState({
    name: '',
    role: '',
    team_id: '',
    custom_role_id: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchTeams();
    fetchRoles();
    fetchPermissionCatalog();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchPermissionCatalog = async () => {
    try {
      const response = await axios.get(`${API}/permissions/catalog`);
      setPermissionCatalog(response.data);
    } catch (error) {
      console.error('Failed to fetch permission catalog:', error);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Please fill all required fields');
      return;
    }
    if (newUser.role === 'custom' && !newUser.custom_role_id) {
      toast.error('Please select a custom role');
      return;
    }

    try {
      await axios.post(`${API}/users`, {
        ...newUser,
        team_id: newUser.team_id || null,
        custom_role_id: newUser.role === 'custom' ? newUser.custom_role_id : null
      });
      toast.success('User created successfully');
      setIsAddDialogOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'reviewer', team_id: '', custom_role_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    if (editUser.role === 'custom' && !editUser.custom_role_id) {
      toast.error('Please select a custom role');
      return;
    }

    try {
      const updateData = {};
      if (editUser.name) updateData.name = editUser.name;
      if (editUser.role) {
        updateData.role = editUser.role;
        updateData.custom_role_id = editUser.role === 'custom' ? editUser.custom_role_id : null;
      }
      if (editUser.team_id !== undefined) updateData.team_id = editUser.team_id || null;

      await axios.put(`${API}/users/${selectedUser.id}`, updateData);
      toast.success('User updated successfully');
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('new_password', newPassword);

      await axios.post(`${API}/auth/reset-password/${selectedUser.id}`, formData);
      toast.success(`Password reset successfully for ${selectedUser.name}`);
      setIsResetDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success(`User ${selectedUser.email} deleted successfully`);
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditUser({
      name: user.name,
      role: user.role,
      team_id: user.team_id || '',
      custom_role_id: user.custom_role_id || ''
    });
    setIsEditDialogOpen(true);
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setIsResetDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', permissions: [] });
    setIsRolesDialogOpen(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, permissions: role.permissions || [] });
    setIsRolesDialogOpen(true);
  };

  const togglePermission = (key) => {
    setRoleForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const handleSaveRole = async () => {
    if (!roleForm.name) {
      toast.error('Role name is required');
      return;
    }
    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, roleForm);
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, roleForm);
        toast.success('Role created');
      }
      setEditingRole(null);
      setRoleForm({ name: '', permissions: [] });
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email) { toast.error('Email is required'); return; }
    if (inviteForm.role === 'custom' && !inviteForm.custom_role_id) {
      toast.error('Please select a custom role'); return;
    }
    setInviteSending(true);
    try {
      const payload = {
        email: inviteForm.email,
        role: inviteForm.role,
        custom_role_id: inviteForm.role === 'custom' ? inviteForm.custom_role_id : null,
        team_id: inviteForm.team_id || null,
      };
      const res = await axios.post(`${API}/users/invite`, payload);
      const token = res.data.token;
      const link = `${window.location.origin}/invite/${token}`;
      setInviteLink(link);
      toast.success('Invitation sent! Copy the link below to share manually if needed.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteSending(false);
    }
  };

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      toast.success('Link copied!');
    }
  };

  const getRoleLabel = (user) => {
    if (user.role === 'custom') {
      const r = roles.find((ro) => ro.id === user.custom_role_id);
      return r ? r.name : 'Custom';
    }
    return user.role;
  };

  const filteredUsers = filterRole === 'all' 
    ? users 
    : users.filter(u => u.role === filterRole);

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : '-';
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              User Management
            </h1>
            <p className="text-slate-600">Manage user accounts, roles and permissions</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-40" data-testid="role-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={openCreateRole}
              variant="outline"
              className="border-slate-300"
              data-testid="manage-roles-btn"
            >
              <Settings2 size={18} className="mr-2" />
              Custom Roles
            </Button>
            <Button
              onClick={() => { setInviteForm({ email: '', role: 'reviewer', custom_role_id: '', team_id: '' }); setInviteLink(null); setIsInviteDialogOpen(true); }}
              variant="outline"
              className="border-indigo-300 text-indigo-700"
              data-testid="invite-user-btn"
            >
              <Mail size={18} className="mr-2" />
              Invite
            </Button>
            <Button 
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-user-btn"
            >
              <Plus size={18} className="mr-2" />
              Add User
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-50 rounded-lg">
                <Users size={24} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Users</p>
                <p className="text-3xl font-bold text-slate-900">{users.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-rose-50 rounded-lg">
                <Shield size={24} className="text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Admins</p>
                <p className="text-3xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'admin').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <UserCircle size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Reviewers</p>
                <p className="text-3xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'reviewer').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Headphones size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Agents</p>
                <p className="text-3xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'agent').length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredUsers.length > 0 ? (
          <Card className="p-6 border border-slate-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Team</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                              <UserCircle size={20} className="text-slate-500" />
                            </div>
                          )}
                          <span className="font-medium text-slate-900">{user.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{user.email}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          user.role === 'admin'
                            ? 'bg-rose-50 text-rose-700 border-rose-200'
                            : user.role === 'agent'
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : user.role === 'custom'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {getRoleLabel(user)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {getTeamName(user.team_id)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(user)}
                            className="border-slate-300"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openResetDialog(user)}
                            className="border-slate-300"
                            data-testid={`reset-password-${user.id}`}
                          >
                            <Key size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDeleteDialog(user)}
                            className="border-rose-300 text-rose-700 hover:bg-rose-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Card className="p-12 text-center border border-slate-200 shadow-sm">
            <Users size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600">No users found</p>
          </Card>
        )}

        {/* Add User Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="add-name">Full Name *</Label>
                <Input
                  id="add-name"
                  placeholder="John Doe"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  data-testid="add-user-name"
                />
              </div>
              <div>
                <Label htmlFor="add-email">Email *</Label>
                <Input
                  id="add-email"
                  type="email"
                  placeholder="john@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  data-testid="add-user-email"
                />
              </div>
              <div>
                <Label htmlFor="add-password">Password *</Label>
                <Input
                  id="add-password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  data-testid="add-user-password"
                />
              </div>
              <div>
                <Label htmlFor="add-role">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="add-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reviewer">Reviewer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="agent">Agent (self-view only)</SelectItem>
                    {roles.length > 0 && <SelectItem value="custom">Custom Role</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {newUser.role === 'custom' && (
                <div>
                  <Label htmlFor="add-custom-role">Custom Role</Label>
                  <Select value={newUser.custom_role_id} onValueChange={(v) => setNewUser({ ...newUser, custom_role_id: v })}>
                    <SelectTrigger data-testid="add-user-custom-role">
                      <SelectValue placeholder="Select a custom role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="add-team">Team (Optional)</Label>
                <Select value={newUser.team_id || "none"} onValueChange={(v) => setNewUser({ ...newUser, team_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="add-user-team">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Team</SelectItem>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddUser} 
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="confirm-add-user"
                >
                  Add User
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600">Email: <span className="font-medium text-slate-900">{selectedUser.email}</span></p>
                </div>
                <div>
                  <Label htmlFor="edit-name">Full Name</Label>
                  <Input
                    id="edit-name"
                    value={editUser.name}
                    onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                    data-testid="edit-user-name"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-role">Role</Label>
                  <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                    <SelectTrigger data-testid="edit-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="agent">Agent (self-view only)</SelectItem>
                      {roles.length > 0 && <SelectItem value="custom">Custom Role</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                {editUser.role === 'custom' && (
                  <div>
                    <Label htmlFor="edit-custom-role">Custom Role</Label>
                    <Select value={editUser.custom_role_id} onValueChange={(v) => setEditUser({ ...editUser, custom_role_id: v })}>
                      <SelectTrigger data-testid="edit-user-custom-role">
                        <SelectValue placeholder="Select a custom role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label htmlFor="edit-team">Team</Label>
                  <Select value={editUser.team_id || "none"} onValueChange={(v) => setEditUser({ ...editUser, team_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-user-team">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Team</SelectItem>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleEditUser} 
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="confirm-edit-user"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-slate-900 mb-1">User: {selectedUser.name}</p>
                  <p className="text-xs text-slate-600">Email: {selectedUser.email}</p>
                </div>
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password (min 6 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setIsResetDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleResetPassword}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    disabled={!newPassword || newPassword.length < 6}
                    data-testid="confirm-reset-button"
                  >
                    Reset Password
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-700">
                <AlertTriangle size={20} />
                Delete User
              </DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                  <p className="text-sm text-rose-800">
                    Are you sure you want to delete this user? This action cannot be undone.
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm"><strong>Name:</strong> {selectedUser.name}</p>
                  <p className="text-sm"><strong>Email:</strong> {selectedUser.email}</p>
                  <p className="text-sm"><strong>Role:</strong> {selectedUser.role}</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteUser}
                    className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="confirm-delete-user"
                  >
                    Delete User
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Custom Roles Dialog */}
        <Dialog open={isRolesDialogOpen} onOpenChange={setIsRolesDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="roles-dialog">
            <DialogHeader>
              <DialogTitle>Custom Roles</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              {roles.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {roles.map((r) => (
                    <div key={r.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid={`role-row-${r.id}`}>
                      <div>
                        <p className="font-medium text-slate-900">{r.name}</p>
                        <p className="text-xs text-slate-500">{(r.permissions || []).length} permissions</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditRole(r)} data-testid={`edit-role-${r.id}`}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={() => handleDeleteRole(r.id)} data-testid={`delete-role-${r.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t border-slate-200 pt-4 space-y-3">
                <p className="text-sm font-semibold text-slate-900">{editingRole ? `Editing: ${editingRole.name}` : 'Create a new role'}</p>
                <div>
                  <Label htmlFor="role-name">Role Name</Label>
                  <Input
                    id="role-name"
                    placeholder="e.g. QA Lead"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    data-testid="role-name-input"
                  />
                </div>
                <div>
                  <Label>Permissions</Label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {permissionCatalog.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`permission-${p.key}`}>
                        <Checkbox
                          checked={roleForm.permissions.includes(p.key)}
                          onCheckedChange={() => togglePermission(p.key)}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  {editingRole && (
                    <Button variant="outline" onClick={() => { setEditingRole(null); setRoleForm({ name: '', permissions: [] }); }} className="flex-1">
                      Cancel Edit
                    </Button>
                  )}
                  <Button onClick={handleSaveRole} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="save-role-btn">
                    {editingRole ? 'Save Role' : 'Create Role'}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite User Dialog */}
        <Dialog open={isInviteDialogOpen} onOpenChange={(v) => { setIsInviteDialogOpen(v); if (!v) setInviteLink(null); }}>
          <DialogContent className="max-w-md" data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail size={18} />
                Invite User by Email
              </DialogTitle>
            </DialogHeader>
            {inviteLink ? (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm font-medium text-emerald-800 mb-1 flex items-center gap-1">
                    <Send size={14} /> Invitation sent!
                  </p>
                  <p className="text-xs text-emerald-700">An email was sent to <strong>{inviteForm.email}</strong>. You can also share the link below.</p>
                </div>
                <div>
                  <Label>Invite Link</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={inviteLink} readOnly className="text-xs" data-testid="invite-link-display" />
                    <Button size="sm" variant="outline" onClick={copyInviteLink} data-testid="copy-invite-link">
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => { setIsInviteDialogOpen(false); setInviteLink(null); }}>
                  Done
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    placeholder="teammate@example.com"
                    value={inviteForm.email}
                    onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                    data-testid="invite-email-input"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={inviteForm.role} onValueChange={v => setInviteForm({ ...inviteForm, role: v })}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="agent">Agent (self-view only)</SelectItem>
                      {roles.length > 0 && <SelectItem value="custom">Custom Role</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                {inviteForm.role === 'custom' && (
                  <div>
                    <Label>Custom Role</Label>
                    <Select value={inviteForm.custom_role_id} onValueChange={v => setInviteForm({ ...inviteForm, custom_role_id: v })}>
                      <SelectTrigger data-testid="invite-custom-role-select">
                        <SelectValue placeholder="Select a custom role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Team (Optional)</Label>
                  <Select value={inviteForm.team_id || 'none'} onValueChange={v => setInviteForm({ ...inviteForm, team_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="invite-team-select">
                      <SelectValue placeholder="No team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Team</SelectItem>
                      {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)} className="flex-1">Cancel</Button>
                  <Button
                    onClick={handleSendInvite}
                    disabled={inviteSending}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="send-invite-btn"
                  >
                    <Send size={16} className="mr-2" />
                    {inviteSending ? 'Sending…' : 'Send Invite'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UserManagement;
